/**
 * Stable identifiers + builders for the AOZONE-19 v2 two-step `/mbr create`
 * modal flow.
 *
 * Flow:
 *   1. `/mbr create` opens the **category picker** modal (this file:
 *      `buildCategoryPickerModalView`). One block: the entry-category select.
 *   2. On submit, the handler responds with `response_action: 'push'` and a
 *      **step-2 modal** (this file: `buildEntryStepModalView`) tailored to
 *      the chosen category per CATEGORY_FIELD_SPEC.
 *
 * Each *_BLOCK_ID / *_ACTION_ID pair maps to a single block in step-2 and is
 * shared between the builder + the submission parser so they cannot drift.
 */

// --- callback ids ----------------------------------------------------------
export const MBR_CATEGORY_CALLBACK_ID = 'mbr_category_modal';
export const MBR_CREATE_CALLBACK_ID = 'mbr_create_modal';
export const MBR_EDIT_CALLBACK_ID = 'mbr_edit_submit';
/** AOZONE-56: "Older…" second modal that asks for free-text YYYY-MM. */
export const MBR_OLDER_PERIOD_CALLBACK_ID = 'mbr_older_period_modal';

// --- block / action ids ----------------------------------------------------
export const ENTRY_CATEGORY_BLOCK_ID = 'entry_category_block';
export const ENTRY_CATEGORY_ACTION_ID = 'entry_category_select';

// AOZONE-56: period picker (step-1 above the category dropdown).
export const MBR_PERIOD_BLOCK_ID = 'mbr_period_block';
export const MBR_PERIOD_ACTION_ID = 'mbr_period_select';
/**
 * Sentinel value emitted by the period select when the user picks the trailing
 * "Older…" option. Distinguished from a real YYYY-MM by being a non-numeric
 * literal — see MBR_PERIOD_REGEX.
 */
export const MBR_PERIOD_OLDER_VALUE = 'OLDER';

// AOZONE-56: older-period second modal — single free-text YYYY-MM input.
export const OLDER_PERIOD_BLOCK_ID = 'older_period_block';
export const OLDER_PERIOD_ACTION_ID = 'older_period_input';

/**
 * AOZONE-56: canonical YYYY-MM format for the mbrPeriod field. Months are
 * zero-padded 01-12; year is a four-digit calendar year.
 */
export const MBR_PERIOD_REGEX = /^\d{4}-(0[1-9]|1[0-2])$/;

export const CUSTOMER_NAME_BLOCK_ID = 'customer_name_block';
export const CUSTOMER_NAME_ACTION_ID = 'customer_name_input';

export const PARTNER_OR_CLOUD_BLOCK_ID = 'partner_or_cloud_block';
export const PARTNER_OR_CLOUD_ACTION_ID = 'partner_or_cloud_input';

export const TARGET_BLOCK_ID = 'target_block';
export const TARGET_ACTION_ID = 'target_input';

export const TITLE_BLOCK_ID = 'title_block';
export const TITLE_ACTION_ID = 'title_input';

export const DEAL_SIZE_AMOUNT_BLOCK_ID = 'deal_size_amount_block';
export const DEAL_SIZE_AMOUNT_ACTION_ID = 'deal_size_amount_input';

export const DEAL_SIZE_TYPE_BLOCK_ID = 'deal_size_type_block';
export const DEAL_SIZE_TYPE_ACTION_ID = 'deal_size_type_select';

export const ACTION_ITEM_BLOCK_ID = 'action_item_block';
export const ACTION_ITEM_ACTION_ID = 'action_item_input';

export const NARRATIVE_BODY_BLOCK_ID = 'narrative_body_block';
export const NARRATIVE_BODY_ACTION_ID = 'narrative_body_input';

// AOZONE-76: "Save & add another" toggle — create step-2 only. When checked,
// a successful submit re-pushes a fresh blank step-2 modal for the same
// category + period instead of tearing down the modal stack.
export const SAVE_ADD_ANOTHER_BLOCK_ID = 'save_add_another_block';
export const SAVE_ADD_ANOTHER_ACTION_ID = 'save_add_another_checkbox';
/** The single checkbox option's value; presence in selected_options = ON. */
export const SAVE_ADD_ANOTHER_VALUE = 'add_another';

// --- category enum + per-category field spec ------------------------------
/**
 * Canonical entry-category labels.
 *
 * NOTE: These are deliberately shorter than the verbiage in
 * `docs/INITIAL_SPEC.md` so each label fits Slack's 24-char modal `title.text`
 * hard limit. The step-2 modal title is the category name itself, so any
 * category longer than 24 chars causes Slack to reject the view with a
 * generic "We had some trouble connecting. Try again?" error. The defensive
 * test in `modalView.test.ts` enforces this — do not add a new category
 * whose label exceeds 24 chars without changing the title strategy.
 */
export const ENTRY_CATEGORIES = [
  'Top Customer Engagements',
  'BD Activities',
  'Market Signals',
  'Enablement Activities',
  'Asks to Accelerate Biz',
  'Other',
] as const;
export type EntryCategory = (typeof ENTRY_CATEGORIES)[number];

export const DEAL_SIZE_TYPE_OPTIONS = ['ARR', 'TCV'] as const;

/**
 * Per-category requirement spec — the canonical AOZONE-19 v2 table.
 * `omit` = field is not shown in step-2 for this category at all.
 * `required` = must be present + non-empty.
 * `optional` (action item only) = shown but blank-allowed.
 */
export interface CategoryFieldSpec {
  customerName: 'required' | 'omit';
  partnerOrCloud: 'required' | 'omit';
  target: 'required' | 'omit';
  title: 'required' | 'omit';
  dealSize: 'required' | 'omit';
  actionItem: 'required' | 'optional' | 'omit';
  narrative: 'required';
}

export const CATEGORY_FIELD_SPEC: Record<EntryCategory, CategoryFieldSpec> = {
  'Top Customer Engagements': {
    customerName: 'required',
    partnerOrCloud: 'omit',
    target: 'omit',
    title: 'omit',
    dealSize: 'required',
    actionItem: 'required',
    narrative: 'required',
  },
  'BD Activities': {
    customerName: 'omit',
    partnerOrCloud: 'required',
    target: 'omit',
    title: 'omit',
    dealSize: 'omit',
    actionItem: 'required',
    narrative: 'required',
  },
  'Market Signals': {
    customerName: 'omit',
    partnerOrCloud: 'omit',
    target: 'omit',
    title: 'required',
    dealSize: 'omit',
    actionItem: 'optional',
    narrative: 'required',
  },
  'Enablement Activities': {
    customerName: 'omit',
    partnerOrCloud: 'omit',
    target: 'required',
    title: 'omit',
    dealSize: 'omit',
    actionItem: 'required',
    narrative: 'required',
  },
  'Asks to Accelerate Biz': {
    customerName: 'omit',
    partnerOrCloud: 'omit',
    target: 'omit',
    title: 'required',
    dealSize: 'omit',
    actionItem: 'optional',
    narrative: 'required',
  },
  Other: {
    customerName: 'omit',
    partnerOrCloud: 'omit',
    target: 'omit',
    title: 'required',
    dealSize: 'omit',
    actionItem: 'optional',
    narrative: 'required',
  },
};

export function isEntryCategory(value: string): value is EntryCategory {
  return (ENTRY_CATEGORIES as readonly string[]).includes(value);
}

// --- view shape ------------------------------------------------------------
interface PlainText {
  type: 'plain_text';
  text: string;
}

interface OptionObject {
  text: PlainText;
  value: string;
}

interface ModalElement {
  type: 'plain_text_input' | 'static_select' | 'radio_buttons' | 'checkboxes';
  action_id: string;
  max_length?: number;
  multiline?: boolean;
  options?: OptionObject[];
  initial_value?: string;
  initial_option?: OptionObject;
  initial_options?: OptionObject[];
}

interface ModalBlock {
  type: 'input';
  block_id: string;
  label: PlainText;
  element: ModalElement;
  optional?: boolean;
  hint?: PlainText;
}

interface ModalView {
  type: 'modal';
  callback_id: string;
  private_metadata: string;
  title: PlainText;
  submit: PlainText;
  close: PlainText;
  blocks: ModalBlock[];
}

function toOptions(values: readonly string[]): OptionObject[] {
  return values.map((value) => ({
    text: { type: 'plain_text', text: value },
    value,
  }));
}

// AOZONE-76: the single "add another" checkbox option. Defined once so the
// checkbox `options` and (when pre-checked) `initial_options` reference the
// same shape — Slack matches a selected option to its option by value+label.
const SAVE_ADD_ANOTHER_OPTION: OptionObject = {
  text: { type: 'plain_text', text: 'Keep this form open for another entry' },
  value: SAVE_ADD_ANOTHER_VALUE,
};

// --- AOZONE-56: mbrPeriod picker options ----------------------------------
const MONTH_SHORT_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * AOZONE-56: build the mbrPeriod static-select options — the current UTC
 * month plus the five preceding months (6 total), followed by a trailing
 * "Older…" entry whose value is `MBR_PERIOD_OLDER_VALUE`. Selecting "Older…"
 * routes the submitter to a second modal that takes a free-text YYYY-MM.
 *
 * Labels are human-readable ("Jun 2026"); values are the canonical YYYY-MM
 * form persisted on the entry row.
 */
export function buildMbrPeriodOptions(now: Date): OptionObject[] {
  const opts: OptionObject[] = [];
  const baseYear = now.getUTCFullYear();
  const baseMonth = now.getUTCMonth(); // 0-11
  for (let i = 0; i < 6; i++) {
    // Date.UTC normalizes month underflow (e.g. month=-1 → prior Dec).
    const d = new Date(Date.UTC(baseYear, baseMonth - i, 1));
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const value = `${y}-${String(m + 1).padStart(2, '0')}`;
    const label = `${MONTH_SHORT_NAMES[m]} ${y}`;
    opts.push({ text: { type: 'plain_text', text: label }, value });
  }
  opts.push({
    text: { type: 'plain_text', text: 'Older…' },
    value: MBR_PERIOD_OLDER_VALUE,
  });
  return opts;
}

// --- step 1: category picker ----------------------------------------------
/**
 * Step-1 modal: an mbrPeriod picker (AOZONE-56) above the entry-category
 * select. The handler's view-submission handler reads both blocks and either
 * pushes the category-specific step-2 modal, or — when the user picked
 * "Older…" — pushes a free-text YYYY-MM modal first.
 *
 * `private_metadata` round-trips `channelId` / `userId` from the slash
 * command so the eventual success ephemeral can be posted in-channel.
 */
export function buildCategoryPickerModalView(
  privateMetadata: string,
  now: Date = new Date(),
  preselectedPeriod?: string,
  preselectedCategory?: string,
): ModalView {
  const periodOptions = buildMbrPeriodOptions(now);
  // Default to the current month — the first option in the list.
  let periodSelectOptions = periodOptions;
  let periodInitial: OptionObject | undefined = periodOptions[0];

  if (preselectedPeriod !== undefined && preselectedPeriod !== MBR_PERIOD_OLDER_VALUE) {
    const existing = periodOptions.find((o) => o.value === preselectedPeriod);
    if (existing) {
      // Pre-select the REAL option so the human label ("Jun 2026") is preserved
      // — Slack matches initial_option to an option by its full shape.
      periodInitial = existing;
    } else if (MBR_PERIOD_REGEX.test(preselectedPeriod)) {
      // AOZONE-76: a custom "Older…" YYYY-MM the user entered previously is a
      // valid period but not in the generated 6-month list. Append it as a
      // selectable option (immutably, ahead of the trailing Older… sentinel)
      // and pre-select it, so the loop round-trips through the picker without
      // forcing the user back through the older-period modal.
      const extra: OptionObject = {
        text: { type: 'plain_text', text: periodOptionLabel(preselectedPeriod) },
        value: preselectedPeriod,
      };
      const sentinelIdx = periodOptions.findIndex((o) => o.value === MBR_PERIOD_OLDER_VALUE);
      const insertAt = sentinelIdx >= 0 ? sentinelIdx : periodOptions.length;
      periodSelectOptions = [
        ...periodOptions.slice(0, insertAt),
        extra,
        ...periodOptions.slice(insertAt),
      ];
      periodInitial = extra;
    }
    // else: the 'OLDER' sentinel or a malformed value — no faithful preselect is
    // possible (a value absent from `options` is rejected), so fall back to the
    // current-month default.
  }

  const periodElement: ModalElement = {
    type: 'static_select',
    action_id: MBR_PERIOD_ACTION_ID,
    options: periodSelectOptions,
  };
  if (periodInitial) {
    periodElement.initial_option = periodInitial;
  }

  const categoryOptions = toOptions(ENTRY_CATEGORIES);
  const categoryElement: ModalElement = {
    type: 'static_select',
    action_id: ENTRY_CATEGORY_ACTION_ID,
    options: categoryOptions,
  };
  if (preselectedCategory !== undefined && isEntryCategory(preselectedCategory)) {
    categoryElement.initial_option = categoryOptions.find((o) => o.value === preselectedCategory);
  }

  return {
    type: 'modal',
    callback_id: MBR_CATEGORY_CALLBACK_ID,
    private_metadata: privateMetadata,
    title: { type: 'plain_text', text: 'New MBR Entry' },
    submit: { type: 'plain_text', text: 'Next' },
    close: { type: 'plain_text', text: 'Cancel' },
    blocks: [
      {
        type: 'input',
        block_id: MBR_PERIOD_BLOCK_ID,
        label: { type: 'plain_text', text: 'MBR Period' },
        element: periodElement,
      },
      {
        type: 'input',
        block_id: ENTRY_CATEGORY_BLOCK_ID,
        label: { type: 'plain_text', text: 'Entry Category' },
        element: categoryElement,
      },
    ],
  };
}

/** AOZONE-76: human-readable "Mon YYYY" label for a canonical YYYY-MM period. */
function periodOptionLabel(value: string): string {
  const [year, month] = value.split('-');
  return `${MONTH_SHORT_NAMES[Number(month) - 1]} ${year}`;
}

/**
 * AOZONE-56: second modal pushed when the user picks "Older…" in step-1.
 * Single free-text input; the submission handler validates the YYYY-MM format
 * server-side via `MBR_PERIOD_REGEX`.
 */
export function buildOlderPeriodModalView(privateMetadata: string): ModalView {
  return {
    type: 'modal',
    callback_id: MBR_OLDER_PERIOD_CALLBACK_ID,
    private_metadata: privateMetadata,
    title: { type: 'plain_text', text: 'Older MBR Period' },
    submit: { type: 'plain_text', text: 'Next' },
    close: { type: 'plain_text', text: 'Cancel' },
    blocks: [
      {
        type: 'input',
        block_id: OLDER_PERIOD_BLOCK_ID,
        label: { type: 'plain_text', text: 'MBR Period (YYYY-MM)' },
        hint: { type: 'plain_text', text: 'e.g. 2025-11' },
        element: {
          type: 'plain_text_input',
          action_id: OLDER_PERIOD_ACTION_ID,
          max_length: 7,
        },
      },
    ],
  };
}

// --- step 2: per-category data entry --------------------------------------
/**
 * Step-2 modal builder. Blocks are determined by CATEGORY_FIELD_SPEC. Order
 * is deterministic + matches the order the field-extraction code reads them.
 *
 * Slack `views.push` stacks this on top of the step-1 modal; pressing Cancel
 * pops back to the category picker.
 */
export interface EntryEditInitialValues {
  customerName?: string;
  partnerOrCloud?: string;
  target?: string;
  title?: string;
  dealSizeAmount?: number;
  dealSizeType?: 'ARR' | 'TCV';
  actionItem?: string;
  narrativeBody?: string;
}

function withInitial(
  el: ModalElement,
  value: string | undefined,
): ModalElement {
  if (value !== undefined && value.length > 0) {
    return { ...el, initial_value: value };
  }
  return el;
}

function withInitialOption(
  el: ModalElement,
  value: string | undefined,
): ModalElement {
  if (value !== undefined) {
    return {
      ...el,
      initial_option: { text: { type: 'plain_text', text: value }, value },
    };
  }
  return el;
}

function buildEntryFormBlocks(
  category: EntryCategory,
  initial?: EntryEditInitialValues,
  showAddAnother = false,
  preCheckAddAnother = false,
): ModalBlock[] {
  const spec = CATEGORY_FIELD_SPEC[category];
  const blocks: ModalBlock[] = [];

  if (spec.customerName === 'required') {
    blocks.push({
      type: 'input',
      block_id: CUSTOMER_NAME_BLOCK_ID,
      label: { type: 'plain_text', text: 'Customer Name' },
      element: withInitial(
        {
          type: 'plain_text_input',
          action_id: CUSTOMER_NAME_ACTION_ID,
          max_length: 100,
        },
        initial?.customerName,
      ),
    });
  }

  if (spec.partnerOrCloud === 'required') {
    blocks.push({
      type: 'input',
      block_id: PARTNER_OR_CLOUD_BLOCK_ID,
      label: { type: 'plain_text', text: 'Partner or Cloud' },
      element: withInitial(
        {
          type: 'plain_text_input',
          action_id: PARTNER_OR_CLOUD_ACTION_ID,
          max_length: 100,
        },
        initial?.partnerOrCloud,
      ),
    });
  }

  if (spec.target === 'required') {
    blocks.push({
      type: 'input',
      block_id: TARGET_BLOCK_ID,
      label: { type: 'plain_text', text: 'Target' },
      hint: {
        type: 'plain_text',
        text: 'Who was enabled — e.g. an internal team or external partner',
      },
      element: withInitial(
        {
          type: 'plain_text_input',
          action_id: TARGET_ACTION_ID,
          max_length: 100,
        },
        initial?.target,
      ),
    });
  }

  if (spec.title === 'required') {
    blocks.push({
      type: 'input',
      block_id: TITLE_BLOCK_ID,
      label: { type: 'plain_text', text: 'Title / Summary' },
      element: withInitial(
        {
          type: 'plain_text_input',
          action_id: TITLE_ACTION_ID,
          max_length: 50,
        },
        initial?.title,
      ),
    });
  }

  if (spec.dealSize === 'required') {
    blocks.push({
      type: 'input',
      block_id: DEAL_SIZE_AMOUNT_BLOCK_ID,
      label: { type: 'plain_text', text: 'Deal Size Amount' },
      element: withInitial(
        {
          type: 'plain_text_input',
          action_id: DEAL_SIZE_AMOUNT_ACTION_ID,
          max_length: 20,
        },
        initial?.dealSizeAmount !== undefined
          ? String(initial.dealSizeAmount)
          : undefined,
      ),
    });
    blocks.push({
      type: 'input',
      block_id: DEAL_SIZE_TYPE_BLOCK_ID,
      label: { type: 'plain_text', text: 'Deal Size Type' },
      element: withInitialOption(
        {
          type: 'radio_buttons',
          action_id: DEAL_SIZE_TYPE_ACTION_ID,
          options: toOptions(DEAL_SIZE_TYPE_OPTIONS),
        },
        initial?.dealSizeType,
      ),
    });
  }

  // Narrative comes immediately before Action Item; Action Item is always the
  // last block in every category that has it (AOZONE-19 v2 polish).
  blocks.push({
    type: 'input',
    block_id: NARRATIVE_BODY_BLOCK_ID,
    label: { type: 'plain_text', text: 'Narrative' },
    element: withInitial(
      {
        type: 'plain_text_input',
        action_id: NARRATIVE_BODY_ACTION_ID,
        multiline: true,
        max_length: 1500,
      },
      initial?.narrativeBody,
    ),
  });

  if (spec.actionItem !== 'omit') {
    blocks.push({
      type: 'input',
      block_id: ACTION_ITEM_BLOCK_ID,
      optional: spec.actionItem === 'optional',
      label: { type: 'plain_text', text: 'Action Item' },
      element: withInitial(
        {
          type: 'plain_text_input',
          action_id: ACTION_ITEM_ACTION_ID,
          multiline: true,
          max_length: 500,
        },
        initial?.actionItem,
      ),
    });
  }

  // AOZONE-76: optional "add another" toggle, always last. `optional: true` is
  // required — Slack rejects a submit that leaves a required checkbox empty.
  // When `preCheckAddAnother` is set (the loop is in progress) it renders
  // pre-checked so the loop continues by default; otherwise it defaults OFF.
  if (showAddAnother) {
    const toggleElement: ModalElement = {
      type: 'checkboxes',
      action_id: SAVE_ADD_ANOTHER_ACTION_ID,
      options: [SAVE_ADD_ANOTHER_OPTION],
    };
    if (preCheckAddAnother) {
      toggleElement.initial_options = [SAVE_ADD_ANOTHER_OPTION];
    }
    blocks.push({
      type: 'input',
      block_id: SAVE_ADD_ANOTHER_BLOCK_ID,
      optional: true,
      label: { type: 'plain_text', text: 'Save & add another' },
      element: toggleElement,
    });
  }

  return blocks;
}

export function buildEntryStepModalView(
  category: EntryCategory,
  privateMetadata: string,
  preCheckAddAnother = false,
): ModalView {
  return {
    type: 'modal',
    callback_id: MBR_CREATE_CALLBACK_ID,
    private_metadata: privateMetadata,
    title: { type: 'plain_text', text: category },
    submit: { type: 'plain_text', text: 'Save' },
    close: { type: 'plain_text', text: 'Cancel' },
    blocks: buildEntryFormBlocks(category, undefined, true, preCheckAddAnother),
  };
}

/**
 * AOZONE-21: edit modal — same block selection as create step-2, but
 * pre-populated with `initialValues` and uses the edit callback id +
 * "Save changes" submit button.
 */
export function buildEntryEditModalView(
  category: EntryCategory,
  privateMetadata: string,
  initialValues: EntryEditInitialValues,
): ModalView {
  return {
    type: 'modal',
    callback_id: MBR_EDIT_CALLBACK_ID,
    private_metadata: privateMetadata,
    title: { type: 'plain_text', text: 'Edit MBR Entry' },
    submit: { type: 'plain_text', text: 'Save changes' },
    close: { type: 'plain_text', text: 'Cancel' },
    blocks: buildEntryFormBlocks(category, initialValues),
  };
}
